import { createElement } from 'react'
import { getCloseTagOf, getSelectionText, isTag, isValidTag } from 'utils'
import {
  CoverLetterScrap,
  GetTagOffsetListReturn,
} from '@app.feature/coverletter/component/CoverLetterDetail/CoverLetterDetailContent/types'
import { COPY_SUFFIX } from 'utils/config'
import {
  buildScrapHighlightNode,
  buildSearchKeywordHighlightNode,
  buildTextNode,
  CoverLetterContentNode,
  CoverLetterNodeType,
  getCoverLetterContentWrapperComponentByName,
} from '../content-node'

export const getTagOffsetList = (
  source: string,
  tag: string,
): GetTagOffsetListReturn[] => {
  let sourceClone = source
  const TAG_LENGTH = tag.length

  if (!tag || !isTag(tag)) {
    return []
  }

  const endTag = getCloseTagOf(tag)

  let index = 0
  const tagOffsetList: GetTagOffsetListReturn[] = []
  while (sourceClone.indexOf(tag) !== -1) {
    const startOffset = sourceClone.indexOf(tag)
    const endOffset = sourceClone.indexOf(endTag) - TAG_LENGTH

    tagOffsetList.push({
      id: index.toString(),
      startOffset,
      endOffset,
    })

    sourceClone = sourceClone.replace(tag, '')
    sourceClone = sourceClone.replace(endTag, '')

    index = index + 1
  }

  return tagOffsetList
}

interface BuildCoverLetterNodeTreeProps {
  content: string
  scraps: CoverLetterScrap[]
  keywords: GetTagOffsetListReturn[]
}

interface TextRun {
  start: number
  end: number
  scrap?: CoverLetterScrap
  keyword?: GetTagOffsetListReturn
}

/**
 * 범위를 기준으로 텍스트를 연속된 덩어리(run)로 분할합니다.
 * 각 run은 하나의 노드로 렌더링되어 React 주석 문제를 방지합니다.
 */
const splitIntoRuns = (
  content: string,
  scraps: CoverLetterScrap[],
  keywords: GetTagOffsetListReturn[],
): TextRun[] => {
  const runs: TextRun[] = []
  const contentLength = content.length

  // 모든 범위의 경계점을 수집 (시작점과 끝점)
  const boundaries = new Set<number>([0, contentLength])
  scraps.forEach(({ startOffset, endOffset }) => {
    boundaries.add(startOffset)
    boundaries.add(endOffset)
  })
  keywords.forEach(({ startOffset, endOffset }) => {
    boundaries.add(startOffset)
    boundaries.add(endOffset)
  })

  // 경계점을 정렬
  const sortedBoundaries = Array.from(boundaries).sort((a, b) => a - b)

  // 각 경계점 사이를 하나의 run으로 생성
  for (let i = 0; i < sortedBoundaries.length - 1; i++) {
    const start = sortedBoundaries[i]
    const end = sortedBoundaries[i + 1]

    if (start >= end) continue

    // 이 run이 어떤 하이라이트에 속하는지 확인
    const scrap = scraps.find(
      ({ startOffset, endOffset }) => startOffset <= start && end <= endOffset,
    )
    const keyword = keywords.find(
      ({ startOffset, endOffset }) => startOffset <= start && end <= endOffset,
    )

    runs.push({
      start,
      end,
      scrap,
      keyword,
    })
  }

  return runs
}

export const buildCoverLetterNodes = (
  source: BuildCoverLetterNodeTreeProps,
) => {
  const { content, scraps, keywords } = source

  // 텍스트를 연속된 덩어리(run)로 분할
  const runs = splitIntoRuns(content, scraps, keywords)

  const nodes: CoverLetterContentNode[] = []

  for (const run of runs) {
    const runText = content.substring(run.start, run.end)

    if (runText.length === 0) {
      continue
    }

    const textNode = buildTextNode(`${run.start}-${run.end}`, runText)

    let currentNode: CoverLetterContentNode = textNode

    if (run.keyword) {
      currentNode = buildSearchKeywordHighlightNode(run.keyword.id, [
        currentNode,
      ])
    }

    if (run.scrap) {
      currentNode = buildScrapHighlightNode(run.scrap.id, [currentNode])
    }

    const lastNode = nodes[nodes.length - 1]

    if (
      lastNode &&
      lastNode.id === currentNode.id &&
      lastNode.type === currentNode.type &&
      lastNode.wrapper === currentNode.wrapper
    ) {
      lastNode.childrenNodeList.push(...currentNode.childrenNodeList)
    } else {
      nodes.push(currentNode)
    }
  }

  return nodes
}

export const isScrapValid = (
  source = '',
  scrap: Omit<CoverLetterScrap, 'id'>,
) => {
  const extractedContent = source.substring(scrap.startOffset, scrap.endOffset)
  return extractedContent === scrap.paragraph
}

export const getRangeBoundingClientRect = (range: Range) => {
  const rects = range.getClientRects()

  if (rects.length < 1) {
    return null
  }

  const initialRect = rects[0]
  const position = {
    left: initialRect.left,
    top: initialRect.top,
    right: initialRect.right,
    bottom: initialRect.bottom,
    width: 0,
    height: 0,
  }

  for (const rect of Array.from(rects)) {
    if (position.left > rect.left) {
      position.left = rect.left
    }

    if (position.top > rect.top) {
      position.top = rect.top
    }

    if (position.right < rect.right) {
      position.right = rect.right
    }

    if (position.bottom < rect.bottom) {
      position.bottom = rect.bottom
    }
  }

  return {
    ...position,
    width: position.right - position.left,
    height: position.bottom - position.top,
  }
}

export const renderCoverLetterContent = (node: CoverLetterContentNode) => {
  switch (node.type) {
    case CoverLetterNodeType.TEXT_NODE: {
      return node.text || ''
    }
    case CoverLetterNodeType.COMPONENT: {
      if (!node.wrapper) {
        return 'error'
      }

      const wrapperComponent = getCoverLetterContentWrapperComponentByName(
        node.wrapper,
      )

      const childrenNode = node.childrenNodeList.map((childrenNode) =>
        renderCoverLetterContent(childrenNode),
      )

      return createElement(
        wrapperComponent,
        {
          key: node.id,
          node,
        },
        childrenNode,
      )
    }
    case CoverLetterNodeType.NATIVE_NODE: {
      if (!node.wrapper) {
        return 'error'
      }

      if (!isValidTag(node.wrapper)) {
        return 'error'
      }

      const childrenNode = node.childrenNodeList.map((childrenNode) =>
        renderCoverLetterContent(childrenNode),
      )

      return createElement(node.wrapper, null, childrenNode)
    }
    default: {
      return 'error'
    }
  }
}

export const getCopyText = (defaultText?: string) => {
  const selectionText = getSelectionText()
  return `${defaultText || selectionText}\n\n\n${COPY_SUFFIX}`
}
